// ============================================================================
// SYUT ENGAGEMENT TYPE SELECT
// ============================================================================
// Domain wrapper around SyutSelect for the Engagement Type master.

import React, { useCallback } from 'react';
import { SyutSelect } from '../SyutSelect';
import { engagementTypeService } from 'api/services/engagementTypeService';
import { useResolvedSeedOptions } from './useResolvedSeedOptions';

export interface SyutEngagementTypeSelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  seedOptions?: { value: string; label: string }[];
  includeInactive?: boolean;
  pageSize?: number;
  disabled?: boolean;
}

export const SyutEngagementTypeSelect: React.FC<SyutEngagementTypeSelectProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  isClearable = false,
  placeholder = 'Search engagement types...',
  size = 'sm',
  seedOptions,
  includeInactive = false,
  pageSize = 500,
  disabled,
}) => {
  // A selected type outside the loaded page would otherwise render blank — see
  // useResolvedSeedOptions for the full account.
  //
  // This master is small today, so the page rarely runs out. The likelier case
  // here is `includeInactive: false` hiding a type that a historical record
  // still points at — the lookup ignores that filter so the stored type still
  // renders, while the picker keeps offering only active ones.
  const fetchOption = useCallback(async (id: string) => {
    const res = await engagementTypeService.getById(id);
    const et: any = res?.data;
    return et ? { value: String(et.id), label: et.name } : null;
  }, []);

  const effectiveSeedOptions = useResolvedSeedOptions(value, seedOptions, fetchOption);

  return (
    <SyutSelect
      label={label}
      value={value}
      isAsync
      loadOptions={async (input: string) => {
        const res = await engagementTypeService.getAll({
          page: 1,
          pageSize,
          searchTerm: input || undefined,
          includeInactive,
        });
        return res.data.map((et: any) => ({ value: et.id, label: et.name }));
      }}
      defaultOptions
      cacheOptions={false}
      seedOptions={effectiveSeedOptions}
      debounceDelay={300}
      minSearchLength={1}
      isClearable={isClearable}
      size={size}
      placeholder={placeholder}
      error={error}
      required={required}
      isDisabled={disabled}
      onChange={(val) => onChange((val as string) || '')}
    />
  );
};

export default SyutEngagementTypeSelect;
